import { useMemo, useState } from "react";
import type { Board } from "../api.js";

/**
 * Narrowing a board without teaching the boards about filtering.
 *
 * `applyFilters` returns a board of the same shape with fewer assignments in
 * it, so the Gantts, their grouping, their pool row and their date maths all
 * carry on unchanged. The alternative — a filter prop threaded through every
 * row builder — would put a second definition of "what is shown" in each one.
 */
export interface BoardFilter {
  resourceId: string;
  teamId: string;
  discipline: string;
  workType: string;
  projectId: string;
  customerId: string;
  lateOnly: boolean;
  text: string;
}

export const EMPTY_FILTER: BoardFilter = {
  resourceId: "", teamId: "", discipline: "", workType: "", projectId: "", customerId: "",
  lateOnly: false, text: "",
};

export const isFiltered = (f: BoardFilter): boolean =>
  Object.entries(f).some(([k, v]) => (k === "lateOnly" ? v === true : v !== ""));

/**
 * Whether the filter names actual people, rather than just narrowing the work.
 *
 * Filtering by team or by a single resource is asking "who's on this team /
 * who is this," and the answer should include someone with nothing currently
 * assigned. Filtering by discipline or work type is asking "what work looks
 * like this," where an idle person naturally has nothing to show and hiding
 * their row is the declutter, not a bug.
 */
export const identityFiltered = (f: BoardFilter): boolean => Boolean(f.teamId || f.resourceId);

export function applyFilters(board: Board, f: BoardFilter): Board {
  if (!isFiltered(f)) return board;

  const phases = new Map(board.phases.map((p) => [p.phaseId, p]));
  const projects = new Map(board.projects.map((p) => [p.projectId, p]));
  const resources = new Map(board.resources.map((r) => [r.resourceId, r]));
  const needle = f.text.trim().toLowerCase();

  const assignments = board.assignments.filter((a) => {
    const p = phases.get(a.phaseId);
    if (!p) return false;
    const project = projects.get(p.projectId);

    if (f.resourceId && a.resourceId !== f.resourceId) return false;
    if (f.teamId) {
      const owner = a.resourceId ? resources.get(a.resourceId) : undefined;
      // Work nobody holds yet stays in view when a team and a project are both
      // chosen: it is what gets dragged onto the team's people, and hiding it
      // left a row of free people with nothing to give them.
      const unheld = !owner || owner.isPool;
      if (!(f.projectId && unheld) && owner?.teamId !== f.teamId) return false;
    }
    if (f.discipline && p.discipline !== f.discipline) return false;
    if (f.workType && p.workType !== f.workType) return false;
    if (f.projectId && p.projectId !== f.projectId) return false;
    if (f.customerId && project?.customerId !== f.customerId) return false;
    // Late means promised before today and still carrying work — the same
    // definition the dashboard's "Late" figure uses.
    if (f.lateOnly && !(p.reqDueDate && p.reqDueDate < board.today && p.remainingHours > 0)) {
      return false;
    }
    if (needle) {
      const hay = [
        p.phaseCode, p.description, p.projectId,
        project?.description, project?.customerId,
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  const keptProjects = new Set(
    assignments.map((a) => phases.get(a.phaseId)?.projectId).filter(Boolean) as string[],
  );

  // Narrow the roster itself only for an identity filter — everyone else's
  // resource list stays full so a Gantt built from it can still choose to
  // drop rows with nothing to show. See `identityFiltered`.
  const keptResources = identityFiltered(f)
    ? board.resources.filter((r) =>
      (!f.teamId || r.teamId === f.teamId) && (!f.resourceId || r.resourceId === f.resourceId))
    : board.resources;

  return {
    ...board,
    assignments,
    projects: board.projects.filter((p) => keptProjects.has(p.projectId)),
    resources: keptResources,
  };
}

export function useBoardFilter() {
  const [filter, setFilter] = useState<BoardFilter>(EMPTY_FILTER);
  return { filter, setFilter };
}

export function FilterBar({ board, filter, onChange, show, mineResourceId }: {
  board: Board;
  filter: BoardFilter;
  onChange: (f: BoardFilter) => void;
  /** Which controls make sense on this board. */
  show: ("person" | "project" | "customer" | "team")[];
  /**
   * The viewer's own resource, when they have one. Renders a one-click preset
   * over the person filter — hidden for an account linked to nobody, where it
   * would filter to nothing and look broken.
   */
  mineResourceId?: string | null;
}) {
  const set = (patch: Partial<BoardFilter>) => onChange({ ...filter, ...patch });

  const customers = useMemo(
    () => [...new Set(board.projects.map((p) => p.customerId).filter(Boolean) as string[])].sort(),
    [board.projects],
  );
  const workTypes = useMemo(
    () => [...new Set(board.phases.map((p) => p.workType).filter(Boolean))].sort(),
    [board.phases],
  );
  const disciplines = useMemo(
    () => [...new Set(board.phases.map((p) => p.discipline).filter(Boolean) as string[])].sort(),
    [board.phases],
  );

  const lateCount = board.phases.filter(
    (p) => p.reqDueDate && p.reqDueDate < board.today && p.remainingHours > 0,
  ).length;

  return (
    <div className="filters">
      {mineResourceId && (
        <button
          className={`btn${filter.resourceId === mineResourceId ? " on" : ""}`}
          onClick={() => set({
            resourceId: filter.resourceId === mineResourceId ? "" : mineResourceId,
          })}
        >
          Mine
        </button>
      )}

      {show.includes("person") && (
        <label>
          person
          <select value={filter.resourceId} onChange={(e) => set({ resourceId: e.target.value })}>
            <option value="">all</option>
            {board.resources.filter((r) => !r.isPool).map((r) => (
              <option key={r.resourceId} value={r.resourceId}>{r.name}</option>
            ))}
          </select>
        </label>
      )}

      {show.includes("project") && (
        <label>
          project
          <select value={filter.projectId} onChange={(e) => set({ projectId: e.target.value })}>
            <option value="">all</option>
            {board.projects.map((p) => (
              <option key={p.projectId} value={p.projectId}>
                {p.projectId}{p.description ? ` — ${p.description.slice(0, 40)}` : ""}
              </option>
            ))}
          </select>
        </label>
      )}

      {show.includes("team") && (
        <label>
          team
          <select value={filter.teamId} onChange={(e) => set({ teamId: e.target.value })}>
            <option value="">all</option>
            {board.teams.map((t) => (
              <option key={t.teamId} value={t.teamId}>{t.name}</option>
            ))}
          </select>
        </label>
      )}

      {show.includes("customer") && (
        <label>
          customer
          <select value={filter.customerId} onChange={(e) => set({ customerId: e.target.value })}>
            <option value="">all</option>
            {customers.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      )}

      <label>
        discipline
        <select value={filter.discipline} onChange={(e) => set({ discipline: e.target.value })}>
          <option value="">all</option>
          {disciplines.map((d) => (
            <option key={d} value={d}>{d.replace("01-DFTG", "")}</option>
          ))}
        </select>
      </label>

      <label>
        work type
        <select value={filter.workType} onChange={(e) => set({ workType: e.target.value })}>
          <option value="">all</option>
          {workTypes.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
      </label>

      <label style={{ gap: 6 }}>
        <input type="checkbox" checked={filter.lateOnly}
          onChange={(e) => set({ lateOnly: e.target.checked })} />
        late only <span className="muted">({lateCount})</span>
      </label>

      <input placeholder="find a project or step…" value={filter.text}
        style={{ minWidth: 190 }}
        onChange={(e) => set({ text: e.target.value })} />

      {isFiltered(filter) && (
        <button className="btn clear" onClick={() => onChange(EMPTY_FILTER)}>Clear</button>
      )}
    </div>
  );
}
